/**
 * ANB BOGOS App Optimizer v3
 * ==========================
 * Purpose: Defer loading of BOGOS (Free Gift) app scripts to improve TBT.
 * Theme: ANB-ella-662-Test-V36
 * 
 * v3 Changes (Dec 17, 2025):
 * - SKIP observer entirely on non-BOGOS pages (homepage, collections, blogs)
 * - Only runs on /products/ and /cart pages where BOGOS is needed
 * - Eliminates ~100 wasted DOM mutations on homepage
 * 
 * v2 Changes (Dec 17, 2025):
 * - LIMITED observer scope to document.head only (was document.documentElement)
 * - ADDED early disconnect after 3s to prevent ongoing observation
 * - OPTIMIZED pattern matching with Set for O(1) lookups
 * - REDUCED callbacks by batching mutations
 * 
 * Targets:
 * - bogos-extension-162/assets/gift.min.js
 * - bogos-extension-162/assets/glider.min.js
 * - bogos-extension-162/assets/freegifts-main.min.css
 */

(function() {
  // Configuration
  const BOGOS_PATTERNS = new Set([
    'bogos-extension',
    'gift.min.js',
    'glider.min.js',
    'freegifts-main',
    'lz-string.min.js'
  ]);
  
  const INTERACTION_EVENTS = ['scroll', 'touchstart', 'click']; // Reduced from 5 to 3 events
  const IDLE_TIMEOUT = 4000; // Fallback load time
  const OBSERVER_MAX_TIME = 3000; // Stop observing after 3s regardless
  
  // State
  let observer;
  let deferredScripts = [];
  let deferredStyles = [];
  let isLoaded = false;
  let mutationCount = 0; // Debug counter
  
  // v3 FIX: Check if BOGOS is needed on this page
  function shouldRunBogos() {
    const path = window.location.pathname;
    // BOGOS typically runs on: product pages, cart pages
    // Skip: homepage, collection pages, blog pages, etc.
    const bogosPages = ['/products/', '/cart'];
    return bogosPages.some(p => path.includes(p));
  }
  
  // Helper: Check if node matches BOGOS patterns (optimized)
  function isBogosNode(node) {
    const tag = node.tagName;
    if (tag !== 'SCRIPT' && tag !== 'LINK') return false;
    
    const src = node.src || node.href || '';
    if (!src) return false;
    
    // O(n) but n is small (5 patterns)
    for (const pattern of BOGOS_PATTERNS) {
      if (src.includes(pattern)) return true;
    }
    return false;
  }
  
  // 1. The Interceptor (MutationObserver) - OPTIMIZED
  function initObserver() {
    if (isLoaded) return;
    
    // v3 FIX: Skip observer entirely on non-BOGOS pages
    const shouldRun = shouldRunBogos();
    if (!shouldRun) {
      console.log('[ANB] BOGOS v3: Skipped on', window.location.pathname, '(not product/cart page)');
      return;
    }
    
    observer = new MutationObserver(mutations => {
      mutationCount++;
      
      // Process all mutations in one batch
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          
          if (isBogosNode(node)) {
            if (node.tagName === 'SCRIPT') {
              node.type = 'text/bogos-deferred';
              deferredScripts.push(node);
            } else if (node.tagName === 'LINK' && node.rel === 'stylesheet') {
              node.rel = 'stylesheet-deferred';
              node.media = 'print';
              deferredStyles.push(node);
            }
          }
        }
      }
    });
    
    // CHANGE: Only observe document.head (not entire documentElement)
    // This dramatically reduces mutation callbacks
    if (document.head) {
      observer.observe(document.head, {
        childList: true,
        subtree: false // Don't need subtree for head
      });
    }
    
    // CHANGE: Auto-disconnect after OBSERVER_MAX_TIME to prevent long-running observation
    setTimeout(() => {
      if (observer && !isLoaded) {
        observer.disconnect();
        console.log('[ANB] BOGOS observer auto-disconnected after', OBSERVER_MAX_TIME, 'ms. Mutations:', mutationCount);
      }
    }, OBSERVER_MAX_TIME);
    
    console.log('[ANB] BOGOS Optimizer v3 initialized (head-only observer)');
  }
  
  // 2. The Activator (Restore scripts)
  function loadBogos() {
    if (isLoaded) return;
    isLoaded = true;

    
    // Stop observing new injections
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    
    console.log(`[ANB] Loading BOGOS... (${deferredScripts.length} scripts, ${deferredStyles.length} styles)`);
    
    // Restore Styles first (to prevent FOUC when scripts run)
    deferredStyles.forEach(link => {
      link.rel = 'stylesheet';
      link.media = 'all';
    });
    
    // Restore Scripts
    // We need to clone and replace to trigger execution
    deferredScripts.forEach(script => {
      const newScript = document.createElement('script');
      
      // Copy attributes
      Array.from(script.attributes).forEach(attr => {
        newScript.setAttribute(attr.name, attr.value);
      });
      
      // Restore executable type
      newScript.type = 'text/javascript';
      
      // Replace in DOM
      if (script.parentNode) {
        script.parentNode.replaceChild(newScript, script);
      } else {
        document.head.appendChild(newScript);
      }
    });
    
    // Cleanup listeners
    INTERACTION_EVENTS.forEach(event => {
      document.removeEventListener(event, loadBogos);
    });
    
    // Dispatch event for any listeners waiting for BOGOS
    document.dispatchEvent(new CustomEvent('anb:bogos-loaded'));
  }
  
  // Initialize
  initObserver();
  
  // Set up triggers
  INTERACTION_EVENTS.forEach(event => {
    document.addEventListener(event, loadBogos, { once: true, passive: true });
  });
  
  // Fallback timeout
  setTimeout(loadBogos, IDLE_TIMEOUT);
  
})();

